import { useEffect, useMemo, useState } from "react";
import { Loader2, CheckCircle2, XCircle, Award, Flag, Camera, X } from "lucide-react";
import api from "@/lib/api";
import ConfidenceStars from "@/components/ConfidenceStars";
import PronoResult from "@/components/PronoResult";
import DateNav, { toISODate } from "@/components/DateNav";

const reunionNum = (r) => { const m = /(\d+)/.exec(r || ""); return m ? parseInt(m[1], 10) : 999; };
const courseNum = (c) => { const m = /(\d+)/.exec(c || ""); return m ? parseInt(m[1], 10) : 999; };
const heureToMin = (h) => { const m = /(\d{1,2})h(\d{2})?/.exec(h || ""); return m ? parseInt(m[1], 10) * 60 + parseInt(m[2] || "0", 10) : 9999; };
const heureSortKey = (h) => { const v = heureToMin(h); return v < 360 ? v + 1440 : v; };

export default function Results() {
  const [selDate, setSelDate] = useState(toISODate());
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photo, setPhoto] = useState(null);
  const [selReunion, setSelReunion] = useState(null);

  const load = (date) => {
    setLoading(true);
    api.get("/results", { params: { date } }).then((r) => setResults(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load(selDate);
  }, []);

  const reunions = useMemo(() => {
    const map = {};
    for (const c of results) {
      const r = c.reunion || "—";
      if (!map[r]) map[r] = { reunion: r, hippodrome: c.hippodrome, heures: [] };
      if (c.heure) map[r].heures.push(c.heure);
    }
    return Object.values(map)
      .map((x) => ({ ...x, firstHeure: [...x.heures].sort((a, b) => heureToMin(a) - heureToMin(b))[0] || "" }))
      .sort((a, b) => reunionNum(a.reunion) - reunionNum(b.reunion));
  }, [results]);

  const displayed = useMemo(() => {
    const list = selReunion ? results.filter((c) => c.reunion === selReunion) : results;
    if (!selReunion) {
      return [...list].sort((a, b) => heureSortKey(a.heure) - heureSortKey(b.heure));
    }
    return [...list].sort((a, b) => {
      const rd = reunionNum(a.reunion) - reunionNum(b.reunion);
      return rd !== 0 ? rd : courseNum(a.course) - courseNum(b.course);
    });
  }, [results, selReunion]);

  const fmtDate = (d) => new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[#39FF14] font-script text-2xl">L'histoire du turf</div>
          <h1 className="text-4xl sm:text-5xl font-black">Résultats passés</h1>
          <p className="text-[#A1B5A8] mt-1 capitalize">{fmtDate(selDate)}</p>
        </div>
        <DateNav value={selDate} onChange={(d) => { setSelDate(d); setSelReunion(null); load(d); }} />
      </div>

      {!loading && reunions.length > 1 && (
        <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="reunion-shortcuts">
          <button
            type="button"
            onClick={() => setSelReunion(null)}
            data-testid="reunion-chip-all"
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${selReunion === null ? "bg-[#00E676] text-[#04180a] border-[#00E676]" : "bg-[#131F17] text-[#A1B5A8] border-[#00E676]/20 hover:border-[#00E676]/50"}`}
          >
            Toutes les réunions
          </button>
          {reunions.map((r) => (
            <button
              key={r.reunion}
              type="button"
              onClick={() => setSelReunion(r.reunion)}
              data-testid={`reunion-chip-${r.reunion}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${selReunion === r.reunion ? "bg-[#00E676] text-[#04180a] border-[#00E676]" : "bg-[#131F17] text-[#E7F5EC] border-[#00E676]/20 hover:border-[#00E676]/50"}`}
            >
              <span className={selReunion === r.reunion ? "text-[#04180a]" : "text-[#39FF14]"}>{r.reunion}</span>
              {r.hippodrome}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#00E676]" /></div>
      ) : results.length === 0 ? (
        <div className="text-center py-24 rounded-xl border border-dashed border-[#00E676]/20 bg-[#131F17]/50 text-[#A1B5A8]">
          Aucun résultat enregistré pour cette date.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="results-list">
          {displayed.map((c) => {
            const base = [...(c.selections || [])].sort((a, b) => b.etoiles - a.etoiles)[0];
            return (
              <div key={c.id} className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-5" data-testid={`result-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-bold flex items-center gap-2">
                    <span className="text-[#39FF14] text-xs">{c.reunion} {c.course}</span>
                    {c.hippodrome}
                    {c.type_pari && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00E676]/15 text-[#39FF14] font-bold">{c.type_pari}</span>}
                  </div>
                  <PronoResult course={c} />
                </div>

                {base && (
                  <div className="flex items-center gap-2 text-sm text-[#A1B5A8] mb-3">
                    <span className="tabular w-6 h-6 grid place-items-center rounded bg-[#1A2B20] text-white font-bold">{base.numero}</span>
                    {base.cheval}
                    <ConfidenceStars value={base.etoiles} size={12} />
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-[#00E676]/10">
                  {c.photo_arrivee && (
                    <button
                      type="button"
                      onClick={() => setPhoto({ url: c.photo_arrivee, title: `${c.reunion} ${c.course} · ${c.hippodrome}` })}
                      data-testid={`photo-arrivee-btn-${c.id}`}
                      title="Voir la photo d'arrivée"
                      className="w-7 h-7 grid place-items-center rounded-md bg-[#00E676]/15 text-[#39FF14] hover:bg-[#00E676]/25 transition-colors"
                    >
                      <Camera size={15} />
                    </button>
                  )}
                  <Flag size={14} className="text-[#697C6F]" />
                  <span className="text-xs text-[#697C6F] uppercase tracking-wide">Arrivée</span>
                  {(c.arrivee || []).slice(0, 3).map((n, i) => (
                    <span
                      key={i}
                      className="tabular w-7 h-7 grid place-items-center rounded-md text-sm font-bold bg-[#1A2B20] text-white"
                    >
                      {n}
                    </span>
                  ))}
                  {(c.rapport_gagnant || c.rapport_place) && (
                    <div className="ml-auto flex items-center gap-1.5" data-testid={`rapports-${c.id}`}>
                      <img src="/pmu-play.webp" alt="PMU Play" title="Rapports PMU" className="h-4 w-auto object-contain shrink-0" />
                      {c.rapport_gagnant && (
                        <span className="text-xs font-black px-2.5 py-1 rounded-md bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30">
                          Gagnant {c.rapport_gagnant}
                        </span>
                      )}
                      {c.rapport_place && (
                        <span className="text-xs font-black px-2.5 py-1 rounded-md bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30">
                          Placé {c.rapport_place}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {photo && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPhoto(null)}
          data-testid="photo-arrivee-lightbox"
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-[#39FF14] flex items-center gap-2"><Camera size={16} /> Photo d'arrivée — {photo.title}</span>
              <button type="button" onClick={() => setPhoto(null)} data-testid="photo-arrivee-close" className="w-8 h-8 grid place-items-center rounded-md bg-[#131F17] text-white hover:bg-[#1A2B20]">
                <X size={18} />
              </button>
            </div>
            <img src={photo.url} alt="Photo d'arrivée" className="w-full rounded-xl border border-[#00E676]/20" data-testid="photo-arrivee-img" />
          </div>
        </div>
      )}
    </div>
  );
}
